import { SectionScore } from "../../model/datasource/score";
import { AttemptId, QuizId } from "../../model/datasource";
import { ScaleOrdinal, scaleOrdinal } from "d3-scale";
import { select, Selection } from "d3-selection";
import { Arc as _Arc, arc, pie, PieArcDatum } from "d3-shape";

interface Dimensions {
	width: number;
	height: number;
	padding: {
		top: number;
		left: number;
		bottom: number;
		right: number;
	};
	totalWidth: number;
	totalHeight: number;
	radius: number;
}

type BackgroundAxis = ScaleOrdinal<string, string>;
type TitleAxis = ScaleOrdinal<string, string>;
type ArcDatum = PieArcDatum<{key: string, value: number}>;
type Arc = _Arc<any, ArcDatum>;
type Chart = Selection<SVGElement, unknown, HTMLElement, any>;
type Tooltip = Selection<HTMLElement, unknown, HTMLElement, any>;
type Segment = Selection<SVGPathElement, ArcDatum, SVGElement, unknown>;

interface Axes {
	background: BackgroundAxis;
	title: TitleAxis;
	arc: Arc;
}

export default class OverviewScoreChart<A extends AttemptId, Z extends QuizId> {
	dimensions: Dimensions;
	colors: string[];
	categories: string[];

	constructor(categories: string[], colors: string[], width: number, height: number, padding: number) {
		width = width || 50; height = height || 50; padding = padding || 2;
		this.dimensions = this.getDimensions(width, height, padding, padding, padding, padding);
		this.colors = colors || ['#28a745', '#dc3545', '#17a2b8'];
		this.categories = categories || ['correct', 'incorrect', 'unanswered'];
	}

	display(parent: HTMLElement, data: SectionScore<A, Z>): void {
		if (data == null) { return; }

		const { background, title, arc } = this.axes(this.categories, this.colors, this.dimensions);
		const pieData = this.formatData(data);

		const chart = this.addChart(
			parent.querySelector<HTMLElement>('svg'),
			this.dimensions
		);
		const tooltip = this.addTooltip(parent);
		const segments = this.addSegments(chart, pieData, arc, background);

		this.addEvents(segments, tooltip, parent, title, data.total_count);
	}

	getDimensions(
		width: number, height: number,
		padTop: number, padLeft: number, padBottom: number, padRight: number
	): Dimensions {
		return {
			width: width,
			height: height,
			padding: { top: padTop, left: padLeft, bottom: padBottom, right: padRight },
			totalWidth: width + padLeft + padRight,
			totalHeight: height + padTop + padBottom,
			radius: Math.min(width, height) / 2
		};
	}

	axes(categories: string[], colors: string[], dimensions: Dimensions): Axes {
		return {
			background: scaleOrdinal<string>()
				.domain(categories)
				.range(colors),
			title: scaleOrdinal<string>()
				.domain(categories)
				.range(['Correct', 'Incorrect', 'Unanswered']),
			arc: arc<ArcDatum>()
				.innerRadius(dimensions.radius * 0.5)
				.outerRadius(dimensions.radius * 1.0)
		};
	}

	addChart(
		selector: HTMLElement, dimensions: Dimensions
	): Chart {
		const svg = select(selector)
			.attr('width', dimensions.totalWidth)
			.attr('height', dimensions.totalHeight);
		return svg.append('g')
			.attr('transform', 'translate(' +
				(dimensions.padding.left + dimensions.width / 2) + ',' +
				(dimensions.padding.top + dimensions.height / 2) +
			')');
	}

	addTooltip(parent: HTMLElement): Tooltip {
		return select(parent)
			.insert('div', ':first-child')
				.style('float', 'left')
				.style('width', 0)
			.insert('div', ':first-child')
				.attr('class', 'border rounded p-1 bg-light text-body')
				.style('width', 'max-content')
				.style('display', 'none')
				.style('z-index', 10)
				.style('position', 'relative');
	}

	formatData(total: SectionScore<A, Z>): ArcDatum[] {
		return pie<any, {key: string, value: number}>()
			.sort(null)
			.value(d => d.value)
			([
				{ key: 'correct', value: parseInt(total.correct_count) },
				{ key: 'incorrect', value: parseInt(total.answered_count) - parseInt(total.correct_count) },
				{ key: 'unanswered', value: parseInt(total.total_count) - parseInt(total.answered_count) }
			]);
	}

	addSegments(
		chart: Chart,
		data: ArcDatum[],
		arc: Arc,
		background: BackgroundAxis
	): Segment {
		return chart.selectAll('g')
			.data(data)
			.enter()
			.append('path')
				.attr('d', arc)
				.attr('fill', d => background(d.data.key))
				.attr('stroke', 'white')
				.style('stroke-width', '2px');
	}

	addEvents(segments: Segment, tooltip: Tooltip, parent: HTMLElement, title: TitleAxis, total: string): Segment {
		return segments.on('mouseover', (e: MouseEvent, d: ArcDatum) => {
			select(e.currentTarget as Element).style('opacity', 0.9);
			tooltip.style('display', 'block');
		})
			.on('mousemove', (e: MouseEvent, d: ArcDatum) => {
				const chartRect = parent.getBoundingClientRect();
				tooltip.style('top', (
					e.pageY - chartRect.y - window.scrollY
				) + 'px')
					.style('left', (
						e.pageX - chartRect.x - window.scrollX + 20
					) + 'px')
					.html(title(d.data.key)+': ' + d.data.value + ' / ' + total);
			})
			.on('mouseout', (e: MouseEvent, d: ArcDatum) => {
				select(e.currentTarget as Element).style('opacity', 1);
				tooltip.style('display', 'none');
			});
	}
}